import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaFacebookF } from "react-icons/fa";
import { FaInstagram } from "react-icons/fa";
import { FaSpotify } from "react-icons/fa";
import GoogleLogo from "../assets/images/google_logo.png";

import { settings } from "../data/settings";
import { Rating } from "@mui/material";
import { fetchGoogleReviewsWithCache } from "../utils/getGoogleData";
import { Link } from "react-router-dom";

const FooterWrapper = styled.footer`
  background-color: #000000;
  color: #fff;
  padding: 3rem 2rem 1rem 2rem;
  font-weight: 100;
  font-size: 0.875rem; !important;
`;

const Columns = styled.div`
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  display: flex;
  flex-wrap: wrap;
  gap: 5rem;
  justify-content: space-between;
  margin: auto;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ColumnTop = styled.div``;

const ColumnBottom = styled.div``;

const Logo = styled.div`
  max-width: 7rem;
  margin-bottom: 0rem;
  margin-left: -1.5rem;
  & img {
    width: 100%;
  }
`;

const SocialIcons = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  align-items: center;

  div {
    background: white;
    height: 1rem;
    width: 1px;
  }
  svg {
    cursor: pointer;
    color: white;
    transition: transform 0.2s ease;

    width: 1.5rem;
    height: 1.5rem;
    &:hover {
      transform: scale(1.1);
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: 4rem;
  text-align: center;
  color: #aaa;
`;

const HourLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const HourItem = styled.div`
  text-transform: uppercase;
`;

const TimeItem = styled.div`
  font-size: 0.875rem;
`;

const FooterTitle = styled.div`
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  margin-top: 2.5rem;
`;

const GoogleRate = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  & img {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.25rem;
  }
  & span {
    font-size: 1.3rem;
  }
`;

const RateValue = styled.div``;

const FooterText = styled.div`
  margin-bottom: ${({ marginBottom }) => marginBottom || "0.5rem"};
  ${({ isBold }) => isBold && "font-weight: bold;"}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  color: ${({ color }) => color || "#fff"};
  text-transform: ${({ textTransform }) => textTransform || "none"};
  text-decoration: none;
  & a {
    text-decoration: none;
    color: ${({ color }) => color || "#fff"};
  }
`;

const Footer = () => {
  const [googleRating, setGoogleRating] = useState(0);

  useEffect(() => {
    const loadRating = async () => {
      const data = await fetchGoogleReviewsWithCache();
      if (data?.globalRating) {
        setGoogleRating(data.globalRating);
      }
    };
    loadRating();
  }, []);

  return (
    <FooterWrapper>
      <Columns maxWidth={settings?.pages?.pageMaxWidth}>
        {/* Présentation */}
        <Column style={{ justifyContent: "space-between" }}>
          <ColumnTop>
            <Logo>{settings?.navbar?.logoImg}</Logo>
            <FooterText
              marginBottom="0.5rem"
              dangerouslySetInnerHTML={{
                __html: settings?.footer?.description || "",
              }}
            />
            <Link
              style={{ textDecoration: "none", color: "unset" }}
              to={settings?.footer?.google}
              target="_blank"
            >
              <GoogleRate>
                <img src={GoogleLogo} alt="google-rate" />
                <Rating
                  name="half-rating-read"
                  value={googleRating.toFixed(1)}
                  precision={0.1}
                  readOnly
                />
                <RateValue>{`${googleRating.toFixed(1)}`}</RateValue>
              </GoogleRate>
            </Link>
          </ColumnTop>
          <ColumnBottom>
            <FooterText variant="body2" marginTop="1rem">
              Suivez-nous :
            </FooterText>
            <SocialIcons>
              <a
                href={settings?.footer.facebook}
                target="_blank"
                rel="noreferrer"
              >
                <FaFacebookF size={20} />
              </a>
              <div></div>
              <a
                href={settings?.footer.instagram}
                target="_blank"
                rel="noreferrer"
              >
                <FaInstagram size={20} />
              </a>
              <div></div>
              <a
                href={"https://open.spotify.com/" + settings?.footer.spotify}
                target="_blank"
                rel="noreferrer"
              >
                <FaSpotify size={20} />
              </a>
            </SocialIcons>
          </ColumnBottom>
        </Column>

        {/* Contact */}
        <Column>
          <FooterTitle>Contact</FooterTitle>
          <FooterText
            dangerouslySetInnerHTML={{
              __html: settings?.footer?.address || "",
            }}
          />
          <FooterText
            color="white"
            textDecoration="none"
            sx={{
              margin: "0.5rem 0rem",
              textDecoration: "none",
              color: "white",
              "& a": {
                textDecoration: "none",
                color: "white",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: settings?.footer?.phone || "",
            }}
          />
          <div>
            <div style={{ fontWeight: "500" }}>Horaires</div>
            {Object.keys(settings?.footer.hours).map((pageKey) => (
              <HourLine key={pageKey}>
                <HourItem>{pageKey}</HourItem>
                <TimeItem>{settings?.footer.hours[pageKey]}</TimeItem>
              </HourLine>
            ))}
          </div>
        </Column>

        {/* Navigation */}
        <Column style={{ flex: "unset !important" }}>
          <FooterTitle>Navigation</FooterTitle>
          {Object.keys(settings?.pages).map((pageKey) => (
            <FooterText
              key={pageKey}
              variant="body2"
              marginBottom="0rem"
              textTransform="uppercase"
            >
              <a
                href={settings?.pages[pageKey].path ?? `/${pageKey}`}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {settings?.pages[pageKey]?.title}
              </a>
            </FooterText>
          ))}
        </Column>
      </Columns>

      {/* Bas de page */}
      <FooterBottom>
        <FooterText>
          Ⓒ {new Date().getFullYear()} {settings?.compagnieName} - Tous droits
          réservés
          <br />
          <a
            href="/mentions-legales"
            style={{ color: "white", textDecoration: "none" }}
          >
            Mentions légales
          </a>
          <br />
          Réalisé avec ❤️ par{" "}
          <a
            href="https://digibuddy.fr/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "white", textDecoration: "none" }}
          >
            DigiBuddy
          </a>
        </FooterText>
      </FooterBottom>
    </FooterWrapper>
  );
};

export default Footer;
